import React, { useEffect, useMemo, useState } from 'react'
import { gql, useMutation, useQuery } from 'urql'
import { DateTime } from 'luxon'
import {
  Alert as MUIAlert,
  Box,
  Button,
  Card,
  Chip,
  Link,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import {
  PhoneInTalk,
  NotificationsActive,
  PauseCircle,
} from '@mui/icons-material'
import AppLink from '../util/AppLink'
import { UserAvatar } from '../util/avatars'
import { DestinationAvatar } from '../util/DestinationAvatar'
import { useConfigValue, useSessionInfo } from '../util/RequireConfig'
import { brandColors, serif } from './brand'
import { Empty, IconTile, Page, Panel, Row, useBrand } from './ui'
import { placeCity, usePlaces, useTimezones } from './tz'
import {
  CallPause,
  ago,
  getCallPauses,
  pauseEnd,
  ackLabel,
  usePolled,
  useAcks,
} from './api'
import EntraSyncPanel from './EntraSyncPanel'

// GoAlert's urql client suspends by default; these pages render their own loading states.
const noSuspense = { suspense: false }

const REFRESH_MS = 30_000

// scrollTo brings a panel below the stats into view (the app bar is fixed).
const scrollTo = (id: string): void =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })

const query = gql`
  query ErecordsOnCallOverview {
    unacked: alerts(
      input: { filterByStatus: [StatusUnacknowledged], first: 1000 }
    ) {
      nodes {
        id
        alertID
        status
        summary
        createdAt
        service {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
      }
    }
    acked: alerts(
      input: { filterByStatus: [StatusAcknowledged], first: 1000 }
    ) {
      nodes {
        id
        alertID
        status
        summary
        createdAt
        service {
          id
          name
        }
      }
      pageInfo {
        hasNextPage
      }
    }
    services(input: { first: 100 }) {
      nodes {
        id
        name
        onCallUsers {
          userID
          userName
          stepNumber
        }
        escalationPolicy {
          id
          name
          repeat
          steps {
            id
            stepNumber
            delayMinutes
            actions {
              type
              args
              displayInfo {
                ... on DestinationDisplayInfo {
                  text
                  iconURL
                  iconAltText
                  linkURL
                }
                ... on DestinationDisplayInfoError {
                  error
                }
              }
            }
          }
        }
      }
    }
    users(input: { first: 100 }) {
      nodes {
        id
        name
        contactMethods {
          id
          name
          disabled
          dest {
            type
            args
          }
        }
        notificationRules {
          id
          delayMinutes
          contactMethodID
        }
      }
    }
  }
`

const ackMutation = gql`
  mutation ErecordsAck($input: UpdateAlertsInput!) {
    updateAlerts(input: $input) {
      id
    }
  }
`

const VOICE = 'builtin-twilio-voice'
const SMS = 'builtin-twilio-sms'
const USER_DEST = 'builtin-user'
const SCHEDULE_DEST = 'builtin-schedule'
const ROTATION_DEST = 'builtin-rotation'

type Dest = { type: string; args: Record<string, string> }
type CM = { id: string; name: string; disabled: boolean; dest: Dest }
type Rule = { id: string; delayMinutes: number; contactMethodID: string }
type User = {
  id: string
  name: string
  contactMethods: CM[]
  notificationRules: Rule[]
}
type Action = Dest & {
  displayInfo: {
    text?: string
    iconURL?: string
    iconAltText?: string
    linkURL?: string
    error?: string
  }
}
type Step = {
  id: string
  stepNumber: number
  delayMinutes: number
  actions: Action[]
}
type EP = { id: string; name: string; repeat: number; steps: Step[] }
type Service = {
  id: string
  name: string
  onCallUsers: { userID: string; userName: string; stepNumber: number }[]
  escalationPolicy: EP | null
}
type OpenAlert = {
  id: string
  alertID: number
  status: 'StatusUnacknowledged' | 'StatusAcknowledged'
  summary: string
  createdAt: string
  service: { id: string; name: string } | null
}

// How a user is reached right now.
type Reach = {
  kind: 'paused' | 'rings' | 'calls-off' | 'sms' | 'app'
  phone: string
  delay: number
  pause?: CallPause
}

function reachOf(
  u: User | undefined,
  pauses: CallPause[],
  twilio: boolean,
): Reach {
  const cms = u?.contactMethods ?? []
  const rules = u?.notificationRules ?? []
  const ruled = (cm: CM): Rule[] =>
    rules.filter((r) => r.contactMethodID === cm.id)
  const voice = cms.filter((c) => c.dest.type === VOICE)
  const sms = cms.filter(
    (c) => c.dest.type === SMS && !c.disabled && ruled(c).length,
  )
  const phone =
    voice[0]?.dest.args.phone_number || sms[0]?.dest.args.phone_number || ''
  // only a paused phone call changes how someone is reached; a paused SMS
  // leaves calls ringing
  const pause = pauses.find((p) => p.userID === u?.id && p.kind !== 'sms')
  if (pause)
    return { kind: 'paused', phone: pause.phone || phone, delay: 0, pause }

  const ringing = voice.filter((c) => !c.disabled && ruled(c).length)
  if (ringing.length) {
    const delay = Math.min(
      ...ringing.flatMap((c) => ruled(c).map((r) => r.delayMinutes)),
    )
    return { kind: twilio ? 'rings' : 'calls-off', phone, delay }
  }
  if (sms.length) return { kind: 'sms', phone, delay: 0 }
  return { kind: 'app', phone, delay: 0 }
}

// destLabel names a step destination; Power Automate webhooks are Teams workflows.
function destLabel(a: Action): string {
  const t = a.displayInfo.text || a.displayInfo.error || a.type
  if (
    a.type === 'builtin-webhook' &&
    /powerplatform\.com|logic\.azure\.com/.test(t)
  ) {
    return 'Teams channel (webhook)'
  }
  return t
}

function severityOf(name = ''): 'critical' | 'warning' | '' {
  const n = name.toLowerCase()
  if (n.includes('critical')) return 'critical'
  if (n.includes('warning')) return 'warning'
  return ''
}

function Stat(props: {
  label: string
  value: React.ReactNode
  tone?: 'bad' | 'warn' | 'ok' | 'muted'
  hint?: string
  to?: string // opens a page
  onClick?: () => void // e.g. scrolls to a panel below
}): React.JSX.Element {
  const theme = useTheme()
  const c = brandColors[theme.palette.mode]
  const clickable = Boolean(props.to || props.onClick)
  const color =
    props.tone === 'bad'
      ? theme.palette.error.main
      : props.tone === 'warn'
        ? c.warn
        : props.tone === 'ok'
          ? c.ok
          : c.ink
  const card = (
    <Card
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: `1px solid ${c.line}`,
        px: 2,
        py: 1.5,
        height: '100%',
        boxSizing: 'border-box',
        ...(clickable
          ? {
              cursor: 'pointer',
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover': { borderColor: c.muted, bgcolor: c.hover },
            }
          : {}),
      }}
    >
      <Typography sx={{ fontSize: 13, color: c.muted }}>
        {props.label}
      </Typography>
      <Typography
        className='erecords-serif'
        sx={{
          fontFamily: serif,
          fontSize: 28,
          lineHeight: 1.2,
          color,
          // Fraunces' default old-style 0 reads as a letter O
          fontVariantNumeric: 'lining-nums tabular-nums',
        }}
      >
        {props.value}
      </Typography>
      {props.hint && (
        <Typography sx={{ fontSize: 12, color: c.muted }}>
          {props.hint}
        </Typography>
      )}
    </Card>
  )
  if (props.to) {
    return (
      <AppLink
        to={props.to}
        underline='none'
        sx={{
          display: 'block',
          height: '100%',
          color: 'inherit',
          '&, &:hover, & *': { textDecoration: 'none' },
        }}
      >
        {card}
      </AppLink>
    )
  }
  if (props.onClick) {
    const go = props.onClick
    return (
      <Box
        role='button'
        tabIndex={0}
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            go()
          }
        }}
        sx={{ height: '100%', borderRadius: '16px' }}
      >
        {card}
      </Box>
    )
  }
  return card
}

function SeverityChip({ name }: { name?: string }): React.JSX.Element | null {
  const sev = severityOf(name)
  if (!sev) return null
  return (
    <Chip
      size='small'
      label={sev === 'critical' ? 'Critical' : 'Warning'}
      color={sev === 'critical' ? 'error' : 'warning'}
      variant='outlined'
      sx={{ height: 20, fontSize: 11 }}
    />
  )
}

function ReachChip({ reach }: { reach: Reach }): React.JSX.Element {
  switch (reach.kind) {
    case 'paused':
      return (
        <Chip
          size='small'
          color='warning'
          icon={<PauseCircle />}
          label={`Calls paused · ${pauseEnd(reach.pause!.until).replace(/ \(.*\)$/, '')}`}
        />
      )
    case 'rings':
      return (
        <Chip
          size='small'
          color='error'
          icon={<PhoneInTalk />}
          label={
            reach.delay ? `Phone rings after ${reach.delay}m` : 'Phone rings'
          }
        />
      )
    case 'calls-off':
      return (
        <Chip
          size='small'
          variant='outlined'
          label='Calls off: no Twilio here'
        />
      )
    case 'sms':
      return <Chip size='small' variant='outlined' label='SMS only' />
    default:
      return <Chip size='small' variant='outlined' label='App / email only' />
  }
}

// groupKey folds repeats of the same alert into one row: same service and the
// same summary once numbers and test run ids are taken out
// ("certificate … expires in -10d 21h" every 4 hours, "BVT FAILED (test_…)").
function groupKey(a: OpenAlert): string {
  const shape = a.summary
    .replace(/\(test_[^)]*\)/g, '')
    .replace(/\d+([.,]\d+)?/g, '#')
    .replace(/\s+/g, ' ')
    .trim()
  return (a.service?.id ?? '') + '|' + shape
}

type AlertGroup = {
  key: string
  service: OpenAlert['service']
  latest: OpenAlert
  oldest: OpenAlert
  count: number
  unacked: number[]
}

function groupAlerts(alerts: OpenAlert[]): AlertGroup[] {
  const m = new Map<string, AlertGroup>()
  for (const a of alerts) {
    const k = groupKey(a)
    const g = m.get(k) ?? {
      key: k,
      service: a.service,
      latest: a,
      oldest: a,
      count: 0,
      unacked: [],
    }
    g.count++
    if (a.createdAt > g.latest.createdAt) g.latest = a
    if (a.createdAt < g.oldest.createdAt) g.oldest = a
    if (a.status === 'StatusUnacknowledged') g.unacked.push(a.alertID)
    m.set(k, g)
  }
  const rank = (g: AlertGroup): number =>
    (g.unacked.length ? 0 : 4) +
    { critical: 0, warning: 1, '': 2 }[severityOf(g.service?.name)]
  return Array.from(m.values()).sort(
    (a, b) =>
      rank(a) - rank(b) || b.latest.createdAt.localeCompare(a.latest.createdAt),
  )
}

const GROUPS_SHOWN = 8

function AlertGroups(props: {
  alerts: OpenAlert[]
  more: boolean
  now: number
  loading: boolean
}): React.JSX.Element {
  const c = useBrand()
  const theme = useTheme()
  const [ack, commitAck] = useMutation(ackMutation)
  const [all, setAll] = useState(false)
  const groups = useMemo(() => groupAlerts(props.alerts), [props.alerts])
  const acks = useAcks(
    groups.filter((g) => !g.unacked.length).map((g) => g.latest.alertID),
  )
  const shown = all ? groups : groups.slice(0, GROUPS_SHOWN)
  const unacked = props.alerts.filter(
    (a) => a.status === 'StatusUnacknowledged',
  ).length
  const hidden = groups.length - GROUPS_SHOWN

  const ackAll = (ids: number[]): void => {
    commitAck(
      { input: { alertIDs: ids, newStatus: 'StatusAcknowledged' } },
      { additionalTypenames: ['Alert'] },
    )
  }

  return (
    <Panel
      flush
      title='Needs attention'
      subtitle={
        props.alerts.length
          ? `${unacked}${props.more ? '+' : ''} unacknowledged · ${props.alerts.length - unacked} acknowledged · ${groups.length} distinct ${groups.length === 1 ? 'problem' : 'problems'}`
          : undefined
      }
      action={
        <Button component={AppLink} to='/alerts' size='small'>
          All alerts
        </Button>
      }
    >
      {props.alerts.length === 0 && !props.loading && (
        <Empty title='All quiet' text='No open alerts right now.' />
      )}
      {shown.map((g) => {
        const sev = severityOf(g.service?.name)
        const open = g.unacked.length > 0
        const accent = !open
          ? c.line
          : sev === 'warning'
            ? c.warn
            : theme.palette.error.main
        return (
          <Row
            key={g.key}
            accent={accent}
            to={`/alerts/${g.latest.alertID}`}
            chevron={!open}
            title={
              <React.Fragment>
                {g.count > 1 && (
                  <Box
                    component='span'
                    sx={{
                      mr: 1,
                      px: 0.75,
                      py: '1px',
                      borderRadius: '6px',
                      fontSize: 12,
                      fontWeight: 600,
                      bgcolor: c.soft,
                      color: c.ink,
                      verticalAlign: 'middle',
                    }}
                  >
                    ×{g.count}
                  </Box>
                )}
                {g.latest.summary}
              </React.Fragment>
            }
            subtitle={
              <React.Fragment>
                {g.service?.name ?? 'No service'}
                {' · '}
                {g.count > 1
                  ? `newest ${ago(g.latest.createdAt, props.now)} ago, first ${ago(g.oldest.createdAt, props.now)} ago`
                  : `${ago(g.latest.createdAt, props.now)} ago`}
                {!open &&
                  (acks[g.latest.alertID]
                    ? ` · acked by ${ackLabel(acks[g.latest.alertID], props.now)}`
                    : ' · acknowledged')}
              </React.Fragment>
            }
            trailing={
              open ? (
                <Button
                  size='small'
                  variant='outlined'
                  disabled={ack.fetching}
                  onClick={() => ackAll(g.unacked)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {g.unacked.length > 1 ? 'Ack all' : 'Ack'}
                </Button>
              ) : undefined
            }
          />
        )
      })}
      {hidden > 0 && (
        <Box
          sx={{ textAlign: 'center', py: 1, borderTop: `1px solid ${c.line}` }}
        >
          <Button size='small' onClick={() => setAll(!all)}>
            {all
              ? 'Show fewer'
              : `Show ${hidden} more ${hidden === 1 ? 'problem' : 'problems'}`}
          </Button>
        </Box>
      )}
      {ack.error && (
        <MUIAlert severity='error' sx={{ m: 2 }}>
          {ack.error.message.replace('[GraphQL] ', '')}
        </MUIAlert>
      )}
    </Panel>
  )
}

// OnCallPeople lists everyone on call once, first responders first, with the
// phone they are reached on and the time where they are.
function OnCallPeople(props: {
  services: Service[]
  userByID: Map<string, User>
  pauses: CallPause[]
  twilio: boolean
  now: number
}): React.JSX.Element {
  const zones = useTimezones()
  const places = usePlaces()
  const myZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const people = new Map<
    string,
    { id: string; name: string; step: number; services: string[] }
  >()
  for (const s of props.services) {
    for (const u of s.onCallUsers) {
      const p = people.get(u.userID) ?? {
        id: u.userID,
        name: u.userName,
        step: u.stepNumber,
        services: [],
      }
      p.step = Math.min(p.step, u.stepNumber)
      if (!p.services.includes(s.name)) p.services.push(s.name)
      people.set(u.userID, p)
    }
  }
  const list = Array.from(people.values()).sort(
    (a, b) => a.step - b.step || a.name.localeCompare(b.name),
  )

  return (
    <Panel
      flush
      title='On call now'
      subtitle={
        props.twilio
          ? props.pauses.length
            ? 'Who gets notified first, and how.'
            : 'Who gets notified first, and how. No calls are paused.'
          : 'Phone calls are off on this instance.'
      }
    >
      {list.length === 0 && (
        <Empty
          title='Nobody on call'
          text='No escalation policy has anyone on it right now.'
        />
      )}
      {list.map((p) => {
        const r = reachOf(props.userByID.get(p.id), props.pauses, props.twilio)
        const zone = zones[p.id]
        const theirs =
          zone && zone !== myZone
            ? DateTime.fromMillis(props.now).setZone(zone).toFormat('HH:mm') +
              ' in ' +
              placeCity(zone, places[p.id])
            : ''
        return (
          <Row
            key={p.id}
            to={`/users/${p.id}`}
            leading={
              <UserAvatar userID={p.id} sx={{ width: 36, height: 36 }} />
            }
            title={p.name}
            subtitle={
              <React.Fragment>
                {p.step === 0 ? 'First to be notified' : `Step ${p.step + 1}`}
                {' · '}
                {p.services.length === 1
                  ? p.services[0]
                  : `${p.services.length} services`}
                {r.phone && ` · ${r.phone}`}
                {theirs && ` · ${theirs}`}
              </React.Fragment>
            }
            trailing={
              // with calls off everywhere the chip would only repeat the
              // panel subtitle on every row
              r.kind === 'calls-off' ? undefined : <ReachChip reach={r} />
            }
          />
        )
      })}
    </Panel>
  )
}

function CallPauses(props: {
  pauses?: CallPause[]
  error?: Error
  now: number
}): React.JSX.Element | null {
  const c = brandColors[useTheme().palette.mode]
  const active = props.pauses ?? []
  if (!props.error && active.length === 0) return null
  return (
    <Panel
      title='Paused calls and SMS'
      subtitle='After an unanswered alert call, calls to that number pause for 60 minutes (SMS still goes out). A phone or SMS can also be paused on the profile.'
    >
      {props.error && (
        <MUIAlert severity='error'>{props.error.message}</MUIAlert>
      )}
      <Stack spacing={1.5}>
        {active.map((p) => (
          <Box
            key={p.id}
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: c.accentSoft,
              display: 'flex',
              gap: 1.5,
            }}
          >
            <UserAvatar userID={p.userID} sx={{ width: 36, height: 36 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, color: c.ink }}>
                {p.userName || 'Unknown user'}{' '}
                <Link
                  href={`tel:${p.phone}`}
                  sx={{ fontWeight: 400, fontSize: 14 }}
                >
                  {p.phone}
                </Link>
              </Typography>
              <Typography sx={{ fontSize: 14, color: c.ink }}>
                {p.kind === 'sms' ? 'SMS' : 'Calls'} paused ·{' '}
                {pauseEnd(p.until, props.now)}
              </Typography>
              <Typography sx={{ fontSize: 13, color: c.muted }}>
                Because:{' '}
                {p.alertID ? (
                  <Link component={AppLink} to={`/alerts/${p.alertID}`}>
                    {p.reason}
                  </Link>
                ) : (
                  p.reason
                )}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Panel>
  )
}

type PolicyView = {
  ep: EP
  services: Service[]
  canRing: boolean
}

function policyViews(
  services: Service[],
  userByID: Map<string, User>,
  pauses: CallPause[],
  twilio: boolean,
): PolicyView[] {
  const byEP = new Map<string, PolicyView>()
  for (const s of services) {
    const ep = s.escalationPolicy
    if (!ep) continue
    const v = byEP.get(ep.id) ?? { ep, services: [], canRing: false }
    v.services.push(s)
    for (const ou of s.onCallUsers) {
      const r = reachOf(userByID.get(ou.userID), pauses, twilio)
      if (r.kind === 'rings' || r.kind === 'paused') v.canRing = true
    }
    byEP.set(ep.id, v)
  }
  return Array.from(byEP.values()).sort(
    (a, b) =>
      Number(b.canRing) - Number(a.canRing) ||
      a.ep.name.localeCompare(b.ep.name),
  )
}

function Routing(props: { views: PolicyView[] }): React.JSX.Element {
  const rows = props.views
    .flatMap((v) => v.services.map((s) => ({ s, v })))
    .sort(
      (a, b) =>
        Number(b.v.canRing) - Number(a.v.canRing) ||
        a.s.name.localeCompare(b.s.name),
    )
  return (
    <Panel
      flush
      title='Warning vs critical'
      subtitle='Which services only notify and which can ring a phone.'
    >
      {rows.map(({ s, v }) => (
        <Row
          key={s.id}
          to={`/services/${s.id}`}
          leading={
            <IconTile tone={v.canRing ? 'bad' : 'muted'}>
              {v.canRing ? <PhoneInTalk /> : <NotificationsActive />}
            </IconTile>
          }
          title={s.name}
          subtitle={`${v.canRing ? 'Can ring a phone' : 'Notify only'} · via ${v.ep.name}`}
          meta={<SeverityChip name={s.name} />}
        />
      ))}
    </Panel>
  )
}

function WhoGetsCalled(props: {
  views: PolicyView[]
  userByID: Map<string, User>
  pauses: CallPause[]
  twilio: boolean
}): React.JSX.Element {
  const c = brandColors[useTheme().palette.mode]
  return (
    <Panel
      title='How alerts escalate'
      subtitle={
        props.twilio
          ? 'Each escalation policy, step by step, with who gets notified and whose phone rings.'
          : 'Twilio is not configured on this instance, so no phone will ring here.'
      }
    >
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        }}
      >
        {props.views.map((v) => {
          let at = 0
          return (
            <Box key={v.ep.id} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  border: `1px solid ${c.line}`,
                  borderRadius: '12px',
                  p: 1.5,
                  height: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Link
                    component={AppLink}
                    to={`/escalation-policies/${v.ep.id}`}
                    sx={{ fontWeight: 600, color: c.ink, flex: 1 }}
                    underline='hover'
                  >
                    {v.ep.name}
                  </Link>
                  <Chip
                    size='small'
                    label={v.canRing ? 'Can ring a phone' : 'Notify only'}
                    color={v.canRing ? 'error' : 'default'}
                    variant={v.canRing ? 'filled' : 'outlined'}
                  />
                </Box>
                <Typography sx={{ fontSize: 12, color: c.muted, mb: 1 }}>
                  For: {v.services.map((s) => s.name).join(', ')}
                </Typography>
                {v.ep.steps.map((st, i) => {
                  const start = at
                  at += st.delayMinutes
                  const people = v.services[0].onCallUsers.filter(
                    (u) => u.stepNumber === st.stepNumber,
                  )
                  const others = st.actions.filter((a) => a.type !== USER_DEST)
                  return (
                    <Box key={st.id} sx={{ mb: 1 }}>
                      <Typography
                        sx={{ fontSize: 12, fontWeight: 600, color: c.muted }}
                      >
                        {i === 0
                          ? 'Right away'
                          : `After ${start} min, if nobody acknowledged`}
                      </Typography>
                      {people.map((p) => {
                        const r = reachOf(
                          props.userByID.get(p.userID),
                          props.pauses,
                          props.twilio,
                        )
                        return (
                          <Box
                            key={p.userID}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            <UserAvatar
                              userID={p.userID}
                              sx={{ width: 28, height: 28 }}
                            />
                            <Link
                              component={AppLink}
                              to={`/users/${p.userID}`}
                              sx={{ color: c.ink }}
                              underline='hover'
                            >
                              {p.userName}
                            </Link>
                            {r.phone && (
                              <Link
                                href={`tel:${r.phone}`}
                                sx={{ fontSize: 13 }}
                              >
                                {r.phone}
                              </Link>
                            )}
                            <ReachChip reach={r} />
                          </Box>
                        )
                      })}
                      {others
                        .filter(
                          (a) =>
                            a.type !== SCHEDULE_DEST &&
                            a.type !== ROTATION_DEST,
                        )
                        .map((a, k) => (
                          <Box
                            key={k}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              py: 0.5,
                            }}
                          >
                            <DestinationAvatar
                              iconURL={a.displayInfo.iconURL}
                              iconAltText={a.displayInfo.iconAltText}
                              error={Boolean(a.displayInfo.error)}
                            />
                            <Typography
                              sx={{
                                fontSize: 14,
                                color: c.ink,
                                minWidth: 0,
                                flex: 1,
                              }}
                              noWrap
                              title={a.displayInfo.text}
                            >
                              {a.displayInfo.linkURL ? (
                                <Link
                                  href={a.displayInfo.linkURL}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  underline='hover'
                                  sx={{ color: c.ink }}
                                >
                                  {destLabel(a)}
                                </Link>
                              ) : (
                                destLabel(a)
                              )}
                            </Typography>
                            <Chip
                              size='small'
                              variant='outlined'
                              label='Message only'
                            />
                          </Box>
                        ))}
                      {others
                        .filter(
                          (a) =>
                            a.type === SCHEDULE_DEST ||
                            a.type === ROTATION_DEST,
                        )
                        .map((a, k) => (
                          <Typography
                            key={'s' + k}
                            sx={{ fontSize: 12, color: c.muted }}
                          >
                            via{' '}
                            {a.type === SCHEDULE_DEST ? 'schedule' : 'rotation'}{' '}
                            {a.displayInfo.text}
                          </Typography>
                        ))}
                      {people.length === 0 && others.length === 0 && (
                        <Typography sx={{ fontSize: 13, color: c.muted }}>
                          Nobody on this step right now.
                        </Typography>
                      )}
                    </Box>
                  )
                })}
                <Typography sx={{ fontSize: 12, color: c.muted }}>
                  {v.ep.repeat > 0
                    ? `Then repeats ${v.ep.repeat} more time${v.ep.repeat > 1 ? 's' : ''}.`
                    : 'Then stops: no repeat.'}
                </Typography>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Panel>
  )
}

// OnCallOverview is the landing page: open alerts, who is on call and whose
// phone rings, paused calls, and warning vs critical routing.
export default function OnCallOverview(): React.JSX.Element {
  const { isAdmin } = useSessionInfo()
  const [twilioEnabled] = useConfigValue('Twilio.Enable')
  const twilio = Boolean(twilioEnabled)

  const [q, refetch] = useQuery({
    context: noSuspense,
    query,
    requestPolicy: 'cache-and-network',
  })
  const pauses = usePolled(getCallPauses, REFRESH_MS)
  const [now, setNow] = useState(Date.now())
  useEffect(() => {
    const t = setInterval(() => {
      setNow(Date.now())
      refetch({ requestPolicy: 'network-only' })
    }, REFRESH_MS)
    return () => clearInterval(t)
  }, [refetch])

  const alerts: OpenAlert[] = useMemo(
    () => [...(q.data?.unacked?.nodes ?? []), ...(q.data?.acked?.nodes ?? [])],
    [q.data],
  )
  const more = Boolean(
    q.data?.unacked?.pageInfo?.hasNextPage ||
      q.data?.acked?.pageInfo?.hasNextPage,
  )
  const services: Service[] = q.data?.services?.nodes ?? []
  const users: User[] = q.data?.users?.nodes ?? []
  const userByID = useMemo(() => new Map(users.map((u) => [u.id, u])), [users])
  const pauseList = pauses.data ?? []
  const views = useMemo(
    () => policyViews(services, userByID, pauseList, twilio),
    [services, userByID, pauseList, twilio],
  )

  const unacked = alerts.filter((a) => a.status === 'StatusUnacknowledged')
  const oldestUnack = unacked.reduce<string | null>(
    (o, a) => (!o || a.createdAt < o ? a.createdAt : o),
    null,
  )
  const activePauses = pauseList.filter((p) => p.kind !== 'sms')
  const smsPauses = pauseList.length - activePauses.length
  const ringers = new Set<string>()
  const onCall = new Set<string>()
  for (const v of views) {
    for (const s of v.services) {
      for (const u of s.onCallUsers) {
        onCall.add(u.userID)
        if (reachOf(userByID.get(u.userID), pauseList, twilio).kind === 'rings')
          ringers.add(u.userID)
      }
    }
  }

  return (
    <Page
      title='Overview'
      subtitle={`Updated ${new Date(now).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · refreshes every 30 seconds`}
      actions={isAdmin ? <EntraSyncPanel compact /> : undefined}
      maxWidth={1400}
    >
      {q.error && (
        <MUIAlert severity='error' sx={{ mb: 2 }}>
          {q.error.message}
        </MUIAlert>
      )}

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          mb: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        <Stat
          label='Unacknowledged'
          value={more ? `${unacked.length}+` : unacked.length}
          tone={unacked.length ? 'bad' : 'ok'}
          to='/alerts?status=unacked'
          hint={
            oldestUnack
              ? `oldest waiting ${ago(oldestUnack, now)}`
              : 'nothing waiting'
          }
        />
        <Stat
          label='Acknowledged, still open'
          value={alerts.length - unacked.length}
          tone='muted'
          to='/alerts?status=acked'
        />
        <Stat
          label='On call now'
          value={onCall.size}
          tone={onCall.size ? 'muted' : 'warn'}
          onClick={() => scrollTo('on-call-now')}
          hint={
            twilio
              ? `${ringers.size} can get a phone call`
              : 'phone calls are off here'
          }
        />
        <Stat
          label='Paused'
          value={pauses.data ? pauseList.length : '…'}
          tone={pauseList.length ? 'warn' : 'muted'}
          onClick={pauseList.length ? () => scrollTo('call-pauses') : undefined}
          hint={
            pauseList.length
              ? [
                  activePauses.length
                    ? `${activePauses.length} call${activePauses.length > 1 ? 's' : ''}`
                    : '',
                  smsPauses ? `${smsPauses} SMS` : '',
                ]
                  .filter(Boolean)
                  .join(', ') +
                ' · ' +
                pauseEnd(pauseList[0].until).replace(/ \(.*\)$/, '')
              : 'no calls or SMS paused'
          }
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          alignItems: 'start',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            lg: 'minmax(0, 8fr) minmax(0, 4fr)',
          },
          // phones: alerts, then who is on call, then the escalation detail
          gridTemplateAreas: {
            xs: '"alerts" "side" "escalate"',
            lg: '"alerts side" "escalate side"',
          },
          gridTemplateRows: { lg: 'auto 1fr' },
        }}
      >
        <Box sx={{ gridArea: 'alerts', minWidth: 0 }}>
          <AlertGroups
            alerts={alerts}
            more={Boolean(more)}
            now={now}
            loading={!q.data}
          />
        </Box>
        <Stack
          spacing={2}
          sx={{
            gridArea: 'side',
            minWidth: 0,
            '& > [id]': { scrollMarginTop: '82px' },
          }}
        >
          <Box id='on-call-now'>
            <OnCallPeople
              services={services}
              userByID={userByID}
              pauses={pauseList}
              twilio={twilio}
              now={now}
            />
          </Box>
          <Box id='call-pauses'>
            <CallPauses pauses={pauses.data} error={pauses.error} now={now} />
          </Box>
          <Routing views={views} />
        </Stack>
        <Box sx={{ gridArea: 'escalate', minWidth: 0 }}>
          <WhoGetsCalled
            views={views}
            userByID={userByID}
            pauses={pauseList}
            twilio={twilio}
          />
        </Box>
      </Box>
    </Page>
  )
}
